/**
 * The doctor dashboard: the BOARD, which is the whole landing screen and the
 * surface the product is being built out from.
 *
 * The copy here belongs to the COMPOSITION, not to the sources. A section that
 * renders Schedule rows will use the Schedule module's words for those rows,
 * and the ribbon of recent Patients uses the Patients module's — otherwise one
 * surface would end up with two vocabularies for one fact, which is the defect
 * the single-owner rule for a domain label exists to prevent.
 *
 * THE BOARD IDS BELOW NAME PRESENTATION, NOT DOMAIN VALUES. Unwired sections
 * render placeholder rows, so their wording is the board's own and stays here.
 * When a read lands, the label for the value it returns moves to the module
 * that owns that domain — Schedule's status vocabulary, the Task module's
 * fields — and the board id is deleted with the placeholder.
 *
 * The widget copy that used to lead this module — the appointment count, the
 * planned Tasks, today's appointments and their two capability refusals — went
 * with the practitioner day page that said it.
 *
 * Composed into the public catalogue by `catalogue.en.ts`. Nothing imports this
 * module directly: consumers ask the catalogue, so how the messages are stored
 * stays this layer's business.
 */
export const dashboard = {
  'dashboard.title': 'Dashboard',

  /* The board -------------------------------------------------------------
     Five sections of one screen, each with a headline figure, and the section
     the clinician is reading below them. */

  'dashboard.boardSections': 'Dashboard sections',
  'dashboard.boardSection': '{title}: {count} {unit}',
  'dashboard.boardSectionSeries': '{title}. {series}. {caption}',
  'dashboard.boardSeriesFigure': '{label}: {count}',

  'dashboard.attentionTitle': 'Needs attention',
  'dashboard.attentionUnit': 'items',
  'dashboard.attentionLink': 'View all',
  'dashboard.attentionFooter': 'View all items',

  'dashboard.dayTitle': 'My schedule today',
  'dashboard.dayUnit': 'appointments',
  'dashboard.dayLink': 'View schedule',
  'dashboard.dayShown': '{shown} of {total} appointments shown',
  'dashboard.dayFilterAll': 'All statuses',
  'dashboard.dayLoading': "Loading today's schedule",
  'dashboard.dayUnavailable': 'Schedule access is unavailable',
  'dashboard.clinicDayTitle': 'Clinic schedule today',
  'dashboard.dayEmpty': 'No appointments today',
  'dashboard.dayEmptyDetail': 'The schedule has no appointments for the server-resolved clinic day.',
  'dashboard.dayFilterEmpty': 'No appointments match this status',
  'dashboard.dayLoadFailed': "Today's schedule could not be loaded.",
  'dashboard.dayStale': 'The schedule could not be refreshed.',
  'dashboard.dayLastConfirmed': 'Last confirmed at {time}',
  'dashboard.dayRetry': 'Retry',

  'dashboard.myTasksTitle': 'My tasks',
  'dashboard.myTasksUnit': 'pending',
  'dashboard.myTasksLink': 'Open tasks',
  'dashboard.myTasksFooter': 'View completed tasks',

  'dashboard.followUpsTitle': 'Open follow-ups',
  'dashboard.followUpsUnit': 'awaiting',
  'dashboard.followUpsLink': 'Review follow-ups',
  'dashboard.followUpsFooter': 'View all follow-ups',

  'dashboard.eventsTitle': "My patients' schedule",
  'dashboard.eventsUnit': 'events',
  'dashboard.eventsLink': 'View patient events',
  'dashboard.eventsFooter': 'View full schedule',

  /* Panel chrome ---------------------------------------------------------- */

  'dashboard.chooseDay': 'Choose a day',
  'dashboard.filter': 'Filter',
  'dashboard.filters': 'Filters',
  'dashboard.sortDueDate': 'Due date',
  'dashboard.scopeAllFollowUps': 'All follow-ups',
  'dashboard.viewList': 'List',
  'dashboard.viewTimeline': 'Timeline',
  'dashboard.columnFollowUp': 'Follow-up',
  'dashboard.columnDue': 'Due date',
  'dashboard.columnEvent': 'Event',
  'dashboard.columnLocation': 'Location',
  'dashboard.taskScopeAll': 'All ({count})',
  'dashboard.taskScopeClinical': 'Clinical ({count})',
  'dashboard.taskScopeAdmin': 'Admin ({count})',
  'dashboard.taskGroupOverdue': 'Overdue ({count})',
  'dashboard.taskGroupDueToday': 'Due today ({count})',
  'dashboard.taskGroupUpcoming': 'Upcoming ({count})',

  /* Row vocabulary -------------------------------------------------------- */

  'dashboard.personMeta': '{age} y · {sex}',
  'dashboard.minutes': '{count} min',
  'dashboard.room': 'Room {number}',
  'dashboard.consultRoom': 'Consult room {number}',
  'dashboard.ward': 'Ward {number}',
  'dashboard.phlebotomy': 'Phlebotomy',
  'dashboard.televisit': 'Televisit',
  'dashboard.dueAt': 'Due {when}',
  'dashboard.yesterday': 'Yesterday',
  'dashboard.tomorrow': 'Tomorrow',
  'dashboard.inDays': 'In {count} days',
  'dashboard.daysAgo': '{count} days ago',

  /* Status vocabulary ------------------------------------------------------
     The board's own presentation states, kept apart from `status.*`, which is
     the Schedule lifecycle a server actually returns. */

  'dashboard.stateNew': 'New',
  'dashboard.stateHigh': 'High',
  'dashboard.stateReview': 'Review',
  'dashboard.stateUnsigned': 'Unsigned',
  'dashboard.stateOverdue': 'Overdue',
  'dashboard.stateResponse': 'Response',
  'dashboard.stateUpcoming': 'Upcoming',
  'dashboard.stateScheduled': 'Scheduled',
  'dashboard.stateInProgress': 'In progress',
  'dashboard.stateCompleted': 'Completed',
  'dashboard.stateAwaiting': 'Awaiting',
  'dashboard.stateAwaitingResult': 'Awaiting result',
  'dashboard.stateAwaitingPatient': 'Awaiting patient',
  'dashboard.stateDueSoon': 'Due soon',
  'dashboard.stateDueToday': 'Due today',

  /* Needs attention ------------------------------------------------------- */

  'dashboard.attentionNewResult': 'New lab result',
  'dashboard.attentionNewResultDetail': 'Complete blood count',
  'dashboard.attentionOutOfRange': 'Result outside the reference range',
  'dashboard.attentionOutOfRangeDetail': 'Potassium',
  'dashboard.attentionNewDocument': 'New document to review',
  'dashboard.attentionNewDocumentDetail': 'Imaging report',
  'dashboard.attentionUnsignedNote': 'Unsigned note',
  'dashboard.attentionUnsignedNoteDetail': 'Progress note',
  'dashboard.attentionOverdueFollowUp': 'Overdue follow-up',
  'dashboard.attentionOverdueFollowUpDetail': 'Follow-up was due {count} days ago',
  'dashboard.attentionMessage': 'Patient awaiting your response',
  'dashboard.attentionMessageDetail': 'Portal message',

  /* My tasks -------------------------------------------------------------- */


  /* Open follow-ups ------------------------------------------------------- */

  'dashboard.followUpResult': 'Waiting for a lab result',
  'dashboard.followUpResultDetail': 'Vitamin B12',
  'dashboard.followUpImagingDisk': 'Patient to send the imaging disk',
  'dashboard.followUpImagingDiskDetail': 'Lumbar spine scan',
  'dashboard.followUpRecheck': 'Recheck in three months',
  'dashboard.followUpRecheckDetail': 'Diabetes follow-up',
  'dashboard.followUpConsult': 'Consult to be scheduled',
  'dashboard.followUpConsultDetail': 'Discuss treatment options',
  'dashboard.followUpBiopsy': 'Review the biopsy report',
  'dashboard.followUpBiopsyDetail': 'Pathology report pending',
  'dashboard.followUpReply': 'Patient message awaiting a reply',
  'dashboard.followUpReplyDetail': 'Portal message from the patient',

  /* My patients' schedule ------------------------------------------------- */

  'dashboard.eventImaging': 'Brain scan',
  'dashboard.eventImagingCategory': 'Imaging',
  'dashboard.eventConsult': 'Specialist consult',
  'dashboard.eventConsultCategory': 'Consultation',
  'dashboard.eventBloodTest': 'Blood test',
  'dashboard.eventBloodTestCategory': 'Pathology',
  'dashboard.eventAdmission': 'Surgical admission',
  'dashboard.eventAdmissionCategory': 'Orthopaedics',
  'dashboard.eventTeleconsultation': 'Teleconsultation',
  'dashboard.eventTeleconsultationCategory': 'Follow-up',
  'dashboard.eventDischarge': 'Discharge visit',
  'dashboard.eventDischargeCategory': 'Internal medicine',
  'dashboard.placeImaging': 'Imaging centre',
  'dashboard.placeClinic': 'Clinic',
  'dashboard.placeLaboratory': 'Laboratory',
  'dashboard.placeHospital': 'Hospital',
  'dashboard.placeVirtual': 'Virtual visit',

  /* The context column ---------------------------------------------------- */

  'dashboard.nextAppointment': 'Next appointment',
  'dashboard.railTitle': 'Summary',
  'dashboard.dayOverview': "Today's overview",
  'dashboard.overviewAppointments': 'Appointments',
  'dashboard.overviewInPerson': 'In person',
  'dashboard.overviewTelevisits': 'Televisits',
  'dashboard.overviewCompleted': 'Completed',
  'dashboard.overviewCheckedIn': 'Checked in',
  'dashboard.overviewWindow': '{from} – {to}',
  'dashboard.todaysPatients': "Today's patients",
  'dashboard.railMore': '+{count} more',
  'dashboard.taskSummary': 'Task summary',
  'dashboard.summaryTotal': 'total',
  'dashboard.summaryTotalRow': 'Total',
  'dashboard.summaryWaitingOnOthers': 'Waiting on others',
  'dashboard.followUpSummary': 'Follow-up summary',
  'dashboard.clinicTimeNote': 'All times are shown in the clinic time zone',

  'dashboard.quickActions': 'Quick actions',
  'dashboard.actionNewLead': 'New lead',
  'dashboard.actionNewLeadDetail': 'Start patient onboarding',
  'dashboard.actionNewTask': 'New task',
  'dashboard.actionNewTaskDetail': 'Create a task',
  'dashboard.actionSendMessage': 'Send message',
  'dashboard.actionSendMessageDetail': 'Write to a patient or doctor',
  'dashboard.actionUploadDocument': 'Upload document',
  'dashboard.actionUploadDocumentDetail': 'Add a file',
  'dashboard.actionCallPatient': 'Call patient',
  'dashboard.actionCallPatientDetail': 'Log a call',
  'dashboard.actionCreateFollowUp': 'Create follow-up',
  'dashboard.actionMessagePatient': 'Message patient',
  'dashboard.actionSendReminder': 'Send reminder',
} as const
